const BASE = '/api';

async function request(path, options = {}) {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });
  if (res.status === 401) {
    
    throw new Error('Unauthorized');
  }
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

const api = {
  login: (username, password) => request('/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) }),
  logout: () => request('/auth/logout', { method: 'POST' }),
  me: () => request('/auth/me'),

  costs: {
    daily: (from, to, service) => request(`/costs/daily?from=${from}&to=${to}${service ? `&service=${service}` : ''}`),
    monthly: (year) => request(`/costs/monthly?year=${year}`),
    summary: (from, to) => request(`/costs/summary?from=${from}&to=${to}`),
    health: () => request('/costs/health'),
  },

  revenue: {
    daily: (from, to, source) => request(`/revenue/daily?from=${from}&to=${to}${source ? `&source=${source}` : ''}`),
    monthly: (year) => request(`/revenue/monthly?year=${year}`),
    downloads: (from, to) => request(`/revenue/downloads?from=${from}&to=${to}`),
  },

  kpi: {
    overview: (from, to) => request(`/kpi/overview?from=${from}&to=${to}`),
    profitTrend: (days) => request(`/kpi/profit-trend?days=${days}`),
  },

  server: {
    latest: () => request('/server/latest'),
    history: (hours) => request(`/server/history?hours=${hours}`),
    live: () => request('/server/live'),
  },

  promo: {
    list: () => request('/promo-codes'),
    stats: () => request('/promo-codes/stats'),
    create: (data) => request('/promo-codes', { method: 'POST', body: JSON.stringify(data) }),
    update: (code, data) => request(`/promo-codes/${code}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (code) => request(`/promo-codes/${code}`, { method: 'DELETE' }),
  },

  promo: {
    list: () => request('/promo-codes'),
    stats: () => request('/promo-codes/stats'),
    create: (data) => request('/promo-codes', { method: 'POST', body: JSON.stringify(data) }),
    update: (code, data) => request(`/promo-codes/${code}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (code) => request(`/promo-codes/${code}`, { method: 'DELETE' }),
  },

  manual: {
    cost: (data) => request('/manual/cost', { method: 'POST', body: JSON.stringify(data) }),
    revenue: (data) => request('/manual/revenue', { method: 'POST', body: JSON.stringify(data) }),
    downloads: (data) => request('/manual/downloads', { method: 'POST', body: JSON.stringify(data) }),
    narrations: (data) => request('/manual/narrations', { method: 'POST', body: JSON.stringify(data) }),
    triggerFetch: () => request('/manual/trigger-fetch', { method: 'POST' }),
    bulkCosts: (entries) => request('/manual/bulk-costs', { method: 'POST', body: JSON.stringify({ entries }) }),
  },
  customPois: {
    list: (includeInactive) => request(`/custom-pois/list?includeInactive=${!!includeInactive}`),
    create: (data) => request('/custom-pois/create', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, updates) => request('/custom-pois/update', { method: 'POST', body: JSON.stringify({ id, updates }) }),
    delete: (id, hard) => request('/custom-pois/delete', { method: 'POST', body: JSON.stringify({ id, hard: !!hard }) }),
    checkOsmDup: (lat, lon, radiusMeters) => request('/custom-pois/check-osm-dup', { method: 'POST', body: JSON.stringify({ lat, lon, radiusMeters }) }),
  },
  userActivity: {
    lookup: ({ email, uid }) => request(`/user-activity/lookup?${email ? 'email=' + encodeURIComponent(email) : 'uid=' + encodeURIComponent(uid)}`),
    active: ({ days, from, to, limit } = {}) => {
      const p = new URLSearchParams();
      if (days) p.set('days', days);
      if (from) p.set('from', from);
      if (to) p.set('to', to);
      if (limit) p.set('limit', limit);
      return request(`/user-activity/active?${p.toString()}`);
    },
    grantPoi: ({ uid, amount, note }) => request('/user-activity/grant-poi', { method: 'POST', body: JSON.stringify({ uid, amount, note }) }),
    // 🆕 Sprint 4
    searchEmail: (q) => request(`/user-activity/search?q=${encodeURIComponent(q)}`),
    notes: {
      list: (uid) => request(`/user-activity/notes/${encodeURIComponent(uid)}`),
      add: (uid, note) => request(`/user-activity/notes/${encodeURIComponent(uid)}`, { method: 'POST', body: JSON.stringify({ note }) }),
      del: (uid, noteId) => request(`/user-activity/notes/${encodeURIComponent(uid)}/${noteId}`, { method: 'DELETE' }),
    },
    directPush: (uid, payload) => request(`/user-activity/push/${encodeURIComponent(uid)}`, { method: 'POST', body: JSON.stringify(payload) }),
    // 🆕 Törlés — előbb előnézet (mit érint), utána a tényleges törlés
    deletePreview: (uid) => request(`/user-activity/delete-preview?uid=${encodeURIComponent(uid)}`),
    deleteUser: (uid, confirmEmail) => request(`/user-activity/user/${encodeURIComponent(uid)}`, {
      method: 'DELETE', body: JSON.stringify({ confirmEmail }),
    }),
  },
  activityMap: {
    recent: (days) => request(`/activity-map?days=${days || 1}`),
    since: (ts) => request(`/activity-map/since?ts=${encodeURIComponent(ts)}`),
  },
  pushStats: {
    summary: (days) => request(`/push-stats?days=${days || 30}`),
  },
  pushSend: {
    preview: (payload) => request('/push-send/preview', { method: 'POST', body: JSON.stringify(payload) }),
    send: (payload) => request('/push-send/send', { method: 'POST', body: JSON.stringify(payload) }),
    test: (payload) => request('/push-send/test', { method: 'POST', body: JSON.stringify(payload) }),
    history: (limit) => request(`/push-send/history?limit=${limit || 50}`),
  },
  settings: {
    get: () => request('/settings'),
    save: (kv) => request('/settings', { method: 'PUT', body: JSON.stringify(kv) }),
    testEmail: (to) => request('/settings/test-email', { method: 'POST', body: JSON.stringify({ to }) }),
    emailHistory: (limit) => request(`/settings/email-history?limit=${limit || 50}`),
  },
  // 🆕 Fázis 3: Affiliate program
  affiliates: {
    list: () => request('/affiliates'),
    create: (data) => request('/affiliates', { method: 'POST', body: JSON.stringify(data) }),
    update: (code, data) => request(`/affiliates/${encodeURIComponent(code)}`, { method: 'PUT', body: JSON.stringify(data) }),
    conversions: (code) => request(`/affiliates/${encodeURIComponent(code)}/conversions`),
    clawback: (code, conversionId) => request(`/affiliates/${encodeURIComponent(code)}/clawback`, { method: 'POST', body: JSON.stringify({ conversionId }) }),
    payout: (code, data) => request(`/affiliates/${encodeURIComponent(code)}/payout`, { method: 'POST', body: JSON.stringify(data || {}) }),
  },
};

export default api;
